import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";

import { I18nProvider } from "@/lib/i18n";
import { AuthProvider } from "@/lib/auth";

import Landing from "@/pages/Landing";
import CookieBanner from "@/components/site/CookieBanner";
import { Toaster } from "@/components/ui/sonner";

// === Code-split heavy routes ============================================
// Public pages (Landing) ship in the main bundle so the LCP is fast.
// Admin / Customer / Legal pages are lazily loaded — these include recharts,
// the admin sidebar, react-hook-form, etc. (~150–200 KB combined). They are
// NEVER loaded on a public landing-page visit.
const ThankYou         = lazy(() => import("@/pages/ThankYou"));
const AdminLogin       = lazy(() => import("@/pages/AdminLogin"));
const AdminDashboard   = lazy(() => import("@/pages/AdminDashboard"));
const AdminResetPassword = lazy(() => import("@/pages/AdminResetPassword"));
const CustomerLogin    = lazy(() => import("@/pages/CustomerLogin"));
const CustomerDashboard = lazy(() => import("@/pages/CustomerDashboard"));
// Radar — internal cockpit, fully code-split (never ships on a public landing visit)
const RadarLogin       = lazy(() => import("@/pages/radar/RadarLogin"));
const RadarLayout      = lazy(() => import("@/pages/radar/RadarLayout"));
const RadarGuard       = lazy(() => import("@/pages/radar/RadarGuard"));
const RadarDashboard   = lazy(() => import("@/pages/radar/RadarDashboard"));
const RadarLeads       = lazy(() => import("@/pages/radar/RadarLeads"));
const RadarReels       = lazy(() => import("@/pages/radar/RadarReels"));
const RadarTrends      = lazy(() => import("@/pages/radar/RadarTrends"));
const RadarPatterns    = lazy(() => import("@/pages/radar/RadarPatterns"));
const RadarIntelligence = lazy(() => import("@/pages/radar/RadarIntelligence"));
const RadarInsights     = lazy(() => import("@/pages/radar/RadarInsights"));
const RadarAttribution  = lazy(() => import("@/pages/radar/RadarAttribution"));
const RadarSettings    = lazy(() => import("@/pages/radar/RadarSettings"));
const ClientPreview    = lazy(() => import("@/pages/preview/ClientPreview"));
const Imprint = lazy(() => import("@/pages/Legal").then(m => ({ default: m.Imprint })));
const Privacy = lazy(() => import("@/pages/Legal").then(m => ({ default: m.Privacy })));

// Tiny inline fallback while a chunk is fetched
function RouteSpinner() {
    return (
        <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
            <div className="size-8 rounded-full bg-[#10B981] animate-pulse" aria-hidden="true" />
        </div>
    );
}

function App() {
    useEffect(() => {
        document.title = "BA Media Performance — Lead Funnel, Ads & Automationen";
    }, []);
    return (
        <div className="App font-body">
            <I18nProvider>
                <AuthProvider>
                    <BrowserRouter>
                        <Suspense fallback={<RouteSpinner />}>
                            <Routes>
                                <Route path="/" element={<Landing />} />
                                <Route path="/thank-you" element={<ThankYou />} />
                                <Route path="/legal/imprint" element={<Imprint />} />
                                <Route path="/legal/privacy" element={<Privacy />} />
                                <Route path="/impressum" element={<Imprint />} />
                                <Route path="/datenschutz" element={<Privacy />} />
                                <Route path="/login" element={<CustomerLogin />} />
                                <Route path="/dashboard/*" element={<CustomerDashboard />} />
                                <Route path="/preview/:slug" element={<ClientPreview />} />
                                <Route path="/admin/login" element={<AdminLogin />} />
                                <Route path="/admin/reset-password" element={<AdminResetPassword />} />
                                <Route path="/admin/dashboard/*" element={<AdminDashboard />} />
                                {/* Radar — internal cockpit. /radar/login is public,
                                    everything below /radar/* is gated by <RadarGuard/>. */}
                                <Route path="/radar/login" element={<RadarLogin />} />
                                <Route
                                    path="/radar"
                                    element={
                                        <RadarGuard>
                                            <RadarLayout />
                                        </RadarGuard>
                                    }
                                >
                                    <Route index element={<RadarDashboard />} />
                                    <Route path="leads" element={<RadarLeads />} />
                                    <Route path="reels" element={<RadarReels />} />
                                    <Route path="trends" element={<RadarTrends />} />
                                    <Route path="patterns" element={<RadarPatterns />} />
                                    <Route path="intelligence" element={<RadarIntelligence />} />
                                    <Route path="insights" element={<RadarInsights />} />
                                    <Route path="attribution" element={<RadarAttribution />} />
                                    <Route path="settings" element={<RadarSettings />} />
                                </Route>
                                <Route path="*" element={<Landing />} />
                            </Routes>
                        </Suspense>
                        {/* Cookie banner mounted globally + sync (DSGVO-critical:
                            must paint BEFORE any tracking script can load). */}
                        <CookieBanner />
                    </BrowserRouter>
                    <Toaster richColors closeButton position="top-center" />
                </AuthProvider>
            </I18nProvider>
        </div>
    );
}

export default App;
